import { Grid, Stack, StackProps, SxProps, Theme, Typography, TypographyProps } from '@mui/material';
import { PropsWithChildren } from 'react';
import { ChevronRight } from '@mui/icons-material';
import { To, useNavigate } from 'react-router';
import { mergeSx } from '../../utils/styles';
import { useSkinStyles } from '../../hooks/useSkinStyles';
import { getHeadingSkinStyles, COMPONENT_SKIN_STYLES } from '../../styles/components/heading';

type HeadingProps = PropsWithChildren &
  StackProps & {
    to?: To;
    wrapperStyles?: SxProps<Theme>;
  };

const Heading = ({ children, to, onClick, sx, wrapperStyles, ...rest }: HeadingProps) => {
  const navigate = useNavigate();
  const cursorIsPointer = Boolean(to || onClick);

  const styles = useSkinStyles(getHeadingSkinStyles(cursorIsPointer));

  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    to && navigate(to);

    onClick?.(e);
  };

  return (
    <Grid sx={mergeSx(styles.headingWrapper, wrapperStyles)} size={12}>
      <Stack
        direction='row'
        onClick={handleClick}
        {...rest}
        sx={mergeSx(
          {
            alignItems: 'center',
            justifyContent: 'space-between',
          },
          styles.heading,
          sx
        )}
      >
        {children}
      </Stack>
    </Grid>
  );
};

const Title = ({ children, ...rest }: TypographyProps) => {
  const styles = useSkinStyles(COMPONENT_SKIN_STYLES);

  return (
    <Typography sx={styles.title} {...rest}>
      {children}
    </Typography>
  );
};

const Subtitle = ({ children, sx, ...rest }: TypographyProps) => {
  const styles = useSkinStyles(COMPONENT_SKIN_STYLES);

  return (
    <Typography {...rest} sx={mergeSx({ color: 'info.main' }, styles.subtitle, sx)}>
      {children}
    </Typography>
  );
};

const Link = ({ children, sx, ...rest }: PropsWithChildren & StackProps) => {
  const styles = useSkinStyles(COMPONENT_SKIN_STYLES);

  return (
    <Stack direction='row' {...rest} sx={mergeSx({ alignItems: 'center' }, styles.linkWrapper, sx)}>
      <Heading.Title>{children}</Heading.Title>
      <ChevronRight sx={styles.linkIcon} />
    </Stack>
  );
};

Heading.Title = Title;
Heading.Subtitle = Subtitle;
Heading.Link = Link;

export default Heading;
